import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import SlideBuilder from '../components/slides/SlideBuilder';
import SlidePreview from '../components/slides/SlidePreview';

const API_BASE = 'https://aptirix-version-610696728606.asia-south1.run.app';

// Build headers including JWT Authorization for course_provider role
const authHeaders = (extra = {}) => {
  const session = SessionManager.getSession('course_provider') || SessionManager.getCurrentSession();
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const CourseProviderDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_courses: 0,
    total_purchases: 0,
    total_revenue: 0
  });
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [managingSlides, setManagingSlides] = useState(null); // {courseId, moduleId, topicId, topicTitle}
  const [slideView, setSlideView] = useState('builder'); // 'builder' or 'preview'
  const [uploadingPPT, setUploadingPPT] = useState(null); // { courseId, moduleId }
  const [pptUploadStatus, setPptUploadStatus] = useState('');
  // Teaching prompts: { "courseId||moduleId": { text, saving, status } }
  const [teachingPrompts, setTeachingPrompts] = useState({});
  
  const initialFormState = {
    title: '',
    category: 'IT & Technology',
    price: '',
    description: '',
    level: 'beginner',
    subtitle: '',
    language: 'English',
    certificate: true,
    what_you_learn: '',
    skills: '',
    // Structured arrays (no more JSON)
    modules: [], // [{ title, topics: [], duration, _pptFile?, _pptUploaded? }]
    instructors: [], // [{ name, title, bio }]
    testimonials: [], // [{ name, role, text, rating }]
    outcomes: [] // [{ metric, label }]
  };
  
  const [editForm, setEditForm] = useState(initialFormState);
  
  // Get provider info from session
  const [providerId, setProviderId] = useState('');
  const [providerName, setProviderName] = useState('');

  useEffect(() => {
    // Check if user is logged in as course provider
    const session = SessionManager.getCurrentSession();
    
    if (!session || session.role !== 'course_provider') {
      navigate('/course_login');
      return;
    }
    
    // Set provider info from session
    const provId = session.profile?.id || session.user_id;
    setProviderId(provId);
    setProviderName(session.profile?.provider_name || session.profile?.company_name || 'Course Provider');
    
    // Get API key from session
    if (session.access_token) {
      // Extract API key from token or fetch it
      fetchApiKey(provId);
    }
    
    loadDashboardData(provId);
  }, [navigate]);

  const fetchApiKey = async (provId) => {
    try {
      const response = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/course-provider/api-key`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.api_key || 'Not available');
      }
    } catch (error) {
      console.error('Error fetching API key:', error);
    }
  };

  const loadDashboardData = async (provId) => {
    if (!provId) return;
    setLoading(true);
    try {
      // Load provider stats
      const statsRes = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/course-provider/dashboard/stats`, { headers: authHeaders() });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Load provider courses
      const coursesRes = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/course-provider/courses`, { headers: authHeaders() });
      if (coursesRes.ok) {
        const coursesData = await coursesRes.json();
        setCourses(coursesData);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course.id);
    setShowCreateForm(false);
    setEditForm({
      title: course.title || '',
      category: course.category || 'IT & Technology',
      price: course.price?.toString() || '',
      description: course.description || '',
      level: course.level || 'beginner',
      subtitle: course.subtitle || '',
      language: course.language || 'English',
      certificate: course.certificate !== false,
      what_you_learn: Array.isArray(course.what_you_learn) ? course.what_you_learn.join('\n') : '',
      skills: Array.isArray(course.skills) ? course.skills.join(', ') : '',
      modules: Array.isArray(course.modules) ? course.modules.map(m => ({
        title: m.title || '',
        topics: Array.isArray(m.topics) ? m.topics : [],
        duration: m.duration || '',
      })) : [],
      instructors: Array.isArray(course.instructors) ? course.instructors : [],
      testimonials: Array.isArray(course.testimonials) ? course.testimonials : [],
      outcomes: Array.isArray(course.outcomes) ? course.outcomes : [],
    });
  };

  const prepareFormData = () => {
    return {
      title: editForm.title,
      provider: providerName,
      category: editForm.category,
      duration_weeks: 1, // Default - AI sessions are typically completed in one sitting
      price: parseFloat(editForm.price),
      description: editForm.description,
      level: editForm.level,
      subtitle: editForm.subtitle || null,
      language: editForm.language,
      certificate: editForm.certificate,
      what_you_learn: editForm.what_you_learn ? editForm.what_you_learn.split('\n').filter(x => x.trim()) : [],
      skills: editForm.skills ? editForm.skills.split(',').map(s => s.trim()).filter(x => x) : [],
      // Modules: strip transient _ fields, add week index for backward compat
      modules: (editForm.modules || []).map((m, idx) => ({
        week: idx + 1,
        title: m.title,
        topics: (m.topics || []).filter(t => t && t.trim()),
        duration: m.duration || '',
      })).filter(m => m.title),
      // Always include "Aptirix AI" as the AI instructor + the course provider as "Prepared by"
      instructors: [
        {
          name: "Aptirix AI",
          title: "AI Course Instructor",
          bio: "Your personal AI teacher - delivers interactive, adaptive lessons with real-time whiteboarding, code examples, quizzes, and challenges.",
          image: "https://lasyrabbit.vercel.app/logos/aptirix_logo.jpg",
          role: "instructor",
        },
        {
          name: providerName || "Course Provider",
          title: "Course Prepared By",
          bio: `This course is curated and published by ${providerName || "the course provider"}.`,
          image: "https://lasyrabbit.vercel.app/logos/aptirix_logo.jpg",
          role: "prepared_by",
        },
      ],
      testimonials: (editForm.testimonials || []).filter(t => t.name),
      outcomes: (editForm.outcomes || []).filter(o => o.metric || o.label),
    };
  };

  const handleUpdateCourse = async (courseId) => {
    if (!providerId) return;
    
    try {
      const data = prepareFormData();
      const response = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/course-provider/courses/${courseId}`, {
        method: 'PUT',
        headers: { ...authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setEditingCourse(null);
        loadDashboardData(providerId);
        alert('Course updated successfully!');
      } else {
        const error = await response.json();
        alert(`Error updating course: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error updating course:', error);
      alert('Error updating course. Please try again.');
    }
  };

  const handleCreateCourse = async () => {
    if (!providerId) return;
    
    try {
      const data = prepareFormData();
      const response = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/course-provider/courses`, {
        method: 'POST',
        headers: { ...authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setShowCreateForm(false);
        setEditForm(initialFormState);
        loadDashboardData(providerId);
        alert('Course created successfully!');
      } else {
        const error = await response.json();
        alert(`Error creating course: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating course:', error);
      alert('Error creating course. Please try again.');
    }
  };

  const handleDeleteCourse = async (courseId, courseTitle) => {
    if (!providerId) return;
    
    if (!window.confirm(`Are you sure you want to delete "${courseTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/course-provider/courses/${courseId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadDashboardData(providerId);
        alert('Course deleted successfully!');
      } else {
        const error = await response.json();
        alert(`Error deleting course: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Error deleting course. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingCourse(null);
    setShowCreateForm(false);
    setEditForm(initialFormState);
  };

  // ─── Module helpers ───
  const addModule = () => {
    setEditForm(prev => ({
      ...prev,
      modules: [...(prev.modules || []), { title: '', topics: [''], duration: '' }]
    }));
  };

  const removeModule = (idx) => {
    setEditForm(prev => ({
      ...prev,
      modules: prev.modules.filter((_, i) => i !== idx)
    }));
  };

  const updateModule = (idx, field, value) => {
    setEditForm(prev => ({
      ...prev,
      modules: prev.modules.map((m, i) => i === idx ? { ...m, [field]: value } : m)
    }));
  };

  const addTopicToModule = (modIdx) => {
    setEditForm(prev => ({
      ...prev,
      modules: prev.modules.map((m, i) => i === modIdx ? { ...m, topics: [...(m.topics || []), ''] } : m)
    }));
  };

  const updateTopic = (modIdx, topicIdx, value) => {
    setEditForm(prev => ({
      ...prev,
      modules: prev.modules.map((m, i) => i === modIdx ? {
        ...m,
        topics: m.topics.map((t, ti) => ti === topicIdx ? value : t)
      } : m)
    }));
  };

  const removeTopic = (modIdx, topicIdx) => {
    setEditForm(prev => ({
      ...prev,
      modules: prev.modules.map((m, i) => i === modIdx ? {
        ...m,
        topics: m.topics.filter((_, ti) => ti !== topicIdx)
      } : m)
    }));
  };

  // ─── Instructor helpers ───
  const addInstructor = () => setEditForm(p => ({ ...p, instructors: [...(p.instructors || []), { name: '', title: '', bio: '' }] }));
  const removeInstructor = (idx) => setEditForm(p => ({ ...p, instructors: p.instructors.filter((_, i) => i !== idx) }));
  const updateInstructor = (idx, field, value) => setEditForm(p => ({
    ...p, instructors: p.instructors.map((it, i) => i === idx ? { ...it, [field]: value } : it)
  }));

  // ─── Testimonial helpers ───
  const addTestimonial = () => setEditForm(p => ({ ...p, testimonials: [...(p.testimonials || []), { name: '', role: '', text: '', rating: 5 }] }));
  const removeTestimonial = (idx) => setEditForm(p => ({ ...p, testimonials: p.testimonials.filter((_, i) => i !== idx) }));
  const updateTestimonial = (idx, field, value) => setEditForm(p => ({
    ...p, testimonials: p.testimonials.map((t, i) => i === idx ? { ...t, [field]: value } : t)
  }));

  // ─── Outcome helpers ───
  const addOutcome = () => setEditForm(p => ({ ...p, outcomes: [...(p.outcomes || []), { metric: '', label: '' }] }));
  const removeOutcome = (idx) => setEditForm(p => ({ ...p, outcomes: p.outcomes.filter((_, i) => i !== idx) }));
  const updateOutcome = (idx, field, value) => setEditForm(p => ({
    ...p, outcomes: p.outcomes.map((o, i) => i === idx ? { ...o, [field]: value } : o)
  }));

  const handlePPTUpload = async (courseId, moduleId, moduleTitle, file) => {
    if (!providerId) return;
    if (!file) return;
    if (!file.name.endsWith('.pptx')) {
      alert('Please select a .pptx file');
      return;
    }

    setUploadingPPT({ courseId, moduleId });
    setPptUploadStatus(`Uploading "${file.name}" for ${moduleTitle}...`);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const url = `https://aptirix-version-610696728606.asia-south1.run.app/api/course-provider/courses/${courseId}/modules/${moduleId}/upload-ppt?topic_id=ppt-all`;

      const response = await fetch(url, { method: 'POST', headers: authHeaders(), body: formData });

      const data = await response.json();
      if (response.ok) {
        setPptUploadStatus(`✅ Extracted ${data.slide_count} slides for ${moduleTitle}`);
        alert(`Successfully uploaded PPT! Extracted ${data.slide_count} slides.`);
      } else {
        setPptUploadStatus(`❌ Error: ${data.detail || 'Upload failed'}`);
        alert(`Upload failed: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading PPT:', error);
      setPptUploadStatus(`❌ Error: ${error.message}`);
      alert(`Upload error: ${error.message}`);
    } finally {
      setTimeout(() => {
        setUploadingPPT(null);
        setPptUploadStatus('');
        // Re-fetch the teaching prompt since PPT upload may have auto-generated one
        fetchTeachingPrompt(courseId, moduleId);
      }, 4000);
    }
  };

  // ─── Teaching prompt handlers ───
  const promptKey = (courseId, moduleId) => `${courseId}||${moduleId}`;

  const fetchTeachingPrompt = async (courseId, moduleId) => {
    if (!courseId || !moduleId) return;
    try {
      const url = `https://aptirix-version-610696728606.asia-south1.run.app/api/course-provider/courses/${courseId}/modules/${moduleId}/teaching-prompt`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setTeachingPrompts(prev => ({
          ...prev,
          [promptKey(courseId, moduleId)]: {
            text: data.teaching_prompt || '',
            saving: false,
            status: data.exists ? (data.auto_generated ? '(auto-generated from PPT)' : '(saved)') : '',
            autoGenerated: data.auto_generated,
          },
        }));
      }
    } catch (e) {
      console.error('Error fetching teaching prompt:', e);
    }
  };

  const updateTeachingPromptText = (courseId, moduleId, text) => {
    setTeachingPrompts(prev => ({
      ...prev,
      [promptKey(courseId, moduleId)]: {
        ...(prev[promptKey(courseId, moduleId)] || {}),
        text,
        status: '',
      },
    }));
  };

  const saveTeachingPrompt = async (courseId, moduleId) => {
    const key = promptKey(courseId, moduleId);
    const entry = teachingPrompts[key];
    if (!entry || !entry.text || !entry.text.trim()) {
      alert('Please enter a teaching prompt before saving.');
      return;
    }
    setTeachingPrompts(prev => ({
      ...prev,
      [key]: { ...prev[key], saving: true, status: 'Saving...' },
    }));
    try {
      const url = `https://aptirix-version-610696728606.asia-south1.run.app/api/course-provider/courses/${courseId}/modules/${moduleId}/teaching-prompt`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ teaching_prompt: entry.text }),
      });
      const data = await res.json();
      if (res.ok) {
        setTeachingPrompts(prev => ({
          ...prev,
          [key]: { ...prev[key], saving: false, status: `✅ Saved (${data.char_count} chars)`, autoGenerated: false },
        }));
      } else {
        setTeachingPrompts(prev => ({
          ...prev,
          [key]: { ...prev[key], saving: false, status: `❌ ${data.detail || 'Save failed'}` },
        }));
      }
    } catch (e) {
      setTeachingPrompts(prev => ({
        ...prev,
        [key]: { ...prev[key], saving: false, status: `❌ ${e.message}` },
      }));
    }
  };

  // Fetch teaching prompts for all modules of the currently-editing course
  useEffect(() => {
    if (!editingCourse || !editForm.modules) return;
    editForm.modules.forEach((_, idx) => {
      const moduleId = `module_${idx + 1}`;
      fetchTeachingPrompt(editingCourse, moduleId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingCourse]);

  const renderCourseForm = (isCreate = false) => (
    <div className="space-y-6 bg-gray-50 p-6 rounded-lg">
      <h3 className="text-lg font-semibold text-gray-900">
        {isCreate ? 'Create New Course' : 'Edit Course'}
      </h3>
      
      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
            type="text"
            value={editForm.title}
            onChange={(e) => setEditForm({...editForm, title: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
          <select
            value={editForm.category}
            onChange={(e) => setEditForm({...editForm, category: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="IT & Technology">IT & Technology</option>
            <option value="Artificial Intelligence">Artificial Intelligence</option>
            <option value="Data Science">Data Science</option>
            <option value="Finance & Banking">Finance & Banking</option>
            <option value="Sales & Marketing">Sales & Marketing</option>
            <option value="HR & Recruitment">HR & Recruitment</option>
            <option value="Business">Business</option>
            <option value="Design">Design</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹) *</label>
          <input
            type="number"
            step="0.01"
            value={editForm.price}
            onChange={(e) => setEditForm({...editForm, price: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Level *</label>
          <select
            value={editForm.level}
            onChange={(e) => setEditForm({...editForm, level: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
          <input
            type="text"
            value={editForm.language}
            onChange={(e) => setEditForm({...editForm, language: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
          <input
            type="text"
            value={editForm.subtitle}
            onChange={(e) => setEditForm({...editForm, subtitle: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="A catchy subtitle for your course"
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
          <textarea
            value={editForm.description}
            onChange={(e) => setEditForm({...editForm, description: e.target.value})}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            required
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">What You'll Learn (one per line)</label>
          <textarea
            value={editForm.what_you_learn}
            onChange={(e) => setEditForm({...editForm, what_you_learn: e.target.value})}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
            placeholder="Understand AI fundamentals&#10;Build machine learning models&#10;Apply AI to real problems"
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Skills (comma-separated)</label>
          <input
            type="text"
            value={editForm.skills}
            onChange={(e) => setEditForm({...editForm, skills: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Python, Machine Learning, TensorFlow, Data Analysis"
          />
        </div>
        
        {/* ─── MODULES (structured UI) ─── */}
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-gray-800">
              📚 Course Modules
              <span className="text-xs text-gray-500 ml-2 font-normal">Add modules with topics. Upload PPT after saving the course.</span>
            </label>
            <button
              type="button"
              onClick={addModule}
              className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
            >
              <Icon name="add" size={14} /> Add Module
            </button>
          </div>
          {(!editForm.modules || editForm.modules.length === 0) && (
            <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded text-sm text-gray-400">
              No modules yet. Click "Add Module" to start.
            </div>
          )}
          <div className="space-y-3">
            {(editForm.modules || []).map((module, modIdx) => {
              const persistedModuleId = `module_${modIdx + 1}`;
              const isUploading = uploadingPPT?.courseId === editingCourse && uploadingPPT?.moduleId === persistedModuleId;
              return (
                <div key={modIdx} className="border border-gray-200 rounded-lg p-3 bg-white">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-gray-500 shrink-0">Module {modIdx + 1}</span>
                    <input
                      type="text"
                      value={module.title}
                      onChange={(e) => updateModule(modIdx, 'title', e.target.value)}
                      placeholder="Module title (e.g., Introduction to Python)"
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeModule(modIdx)}
                      className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                    >
                      <Icon name="delete" size={14} />
                    </button>
                  </div>
                  {/* Topics */}
                  <div className="ml-4 space-y-1">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Topics</div>
                    {(module.topics || []).map((topic, topicIdx) => (
                      <div key={topicIdx} className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-400 w-4">{topicIdx + 1}.</span>
                        <input
                          type="text"
                          value={topic}
                          onChange={(e) => updateTopic(modIdx, topicIdx, e.target.value)}
                          placeholder="Topic name"
                          className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => removeTopic(modIdx, topicIdx)}
                          className="px-1 py-0.5 text-[10px] text-red-500 hover:bg-red-50 rounded"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addTopicToModule(modIdx)}
                      className="text-[10px] text-blue-600 hover:underline ml-5"
                    >
                      + Add topic
                    </button>
                  </div>
                  {/* PPT Upload (only when editing existing course) */}
                  {editingCourse && (
                    <div className="mt-2 ml-4 pt-2 border-t border-gray-100 flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 uppercase tracking-wider">PPT:</span>
                      <label className={`cursor-pointer inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded transition-colors ${isUploading ? 'bg-gray-300 text-gray-500 cursor-wait' : 'bg-purple-500 text-white hover:bg-purple-600'}`}>
                        <Icon name="upload" size={12} />
                        {isUploading ? 'Uploading...' : 'Upload .pptx'}
                        <input
                          type="file"
                          accept=".pptx"
                          className="hidden"
                          disabled={isUploading}
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handlePPTUpload(editingCourse, persistedModuleId, module.title || `Module ${modIdx + 1}`, e.target.files[0]);
                              e.target.value = '';
                            }
                          }}
                        />
                      </label>
                      <span className="text-[10px] text-gray-400">AI will teach from these slides</span>
                    </div>
                  )}

                  {/* AI Teaching Prompt (only when editing existing course) */}
                  {editingCourse && (() => {
                    const key = `${editingCourse}||${persistedModuleId}`;
                    const entry = teachingPrompts[key] || { text: '', saving: false, status: '', autoGenerated: false };
                    return (
                      <div className="mt-2 ml-4 pt-2 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-gray-600 uppercase tracking-wider font-semibold">
                            🧠 AI Teaching Prompt
                          </span>
                          {entry.status && (
                            <span className={`text-[10px] ${entry.status.startsWith('✅') ? 'text-green-600' : entry.status.startsWith('❌') ? 'text-red-600' : 'text-gray-500'}`}>
                              {entry.status}
                            </span>
                          )}
                        </div>
                        <textarea
                          value={entry.text}
                          onChange={(e) => updateTeachingPromptText(editingCourse, persistedModuleId, e.target.value)}
                          placeholder="Tell the AI how to teach this module, slide by slide. E.g., 'Slide 1: Introduce Python and mention its use in data science. Slide 2: Walk through variables with int and string examples...'"
                          rows={6}
                          className="w-full px-2 py-1 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 font-mono"
                        />
                        <div className="flex items-center gap-2 mt-1">
                          <button
                            type="button"
                            onClick={() => saveTeachingPrompt(editingCourse, persistedModuleId)}
                            disabled={entry.saving}
                            className="px-3 py-1 text-[10px] bg-purple-500 text-white rounded hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-wait"
                          >
                            {entry.saving ? 'Saving...' : '💾 Save Prompt'}
                          </button>
                          {entry.autoGenerated && (
                            <span className="text-[10px] text-amber-600">⚠️ Auto-generated — review and edit before saving</span>
                          )}
                          <span className="text-[10px] text-gray-400 ml-auto">{(entry.text || '').length} chars</span>
                        </div>
                      </div>
                    );
                  })()}

                  {!editingCourse && (
                    <div className="mt-2 ml-4 pt-2 border-t border-gray-100 text-[10px] text-gray-400">
                      💡 Save the course first, then upload PPTs and write teaching prompts per module
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── INSTRUCTOR INFO (auto-added: Aptirix AI + Course Provider) ─── */}
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-gray-800">👨‍🏫 Instructor & Course Author</label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="border border-purple-200 rounded p-3 bg-purple-50 flex items-center gap-3">
              <img
                src="https://lasyrabbit.vercel.app/logos/aptirix_logo.jpg"
                alt="Aptirix AI"
                className="h-12 w-12 rounded-full object-cover border-2 border-white shadow"
              />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 text-sm">Aptirix AI</div>
                <div className="text-[10px] text-gray-600">AI Course Instructor</div>
                <div className="text-[9px] text-purple-600 mt-0.5">✓ Auto-added — teaches the course</div>
              </div>
            </div>
            <div className="border border-blue-200 rounded p-3 bg-blue-50 flex items-center gap-3">
              <img
                src="https://lasyrabbit.vercel.app/logos/aptirix_logo.jpg"
                alt={providerName}
                className="h-12 w-12 rounded-full object-cover border-2 border-white shadow"
              />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 text-sm truncate">{providerName || 'Course Provider'}</div>
                <div className="text-[10px] text-gray-600">Course Prepared By</div>
                <div className="text-[9px] text-blue-600 mt-0.5">✓ Auto-added — your name as the author</div>
              </div>
            </div>
          </div>
        </div>

        {/* ─── TESTIMONIALS (structured UI) ─── */}
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-gray-800">⭐ Testimonials <span className="text-xs text-gray-500 font-normal">Optional</span></label>
            <button type="button" onClick={addTestimonial} className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1">
              <Icon name="add" size={14} /> Add Testimonial
            </button>
          </div>
          <div className="space-y-2">
            {(editForm.testimonials || []).map((t, idx) => (
              <div key={idx} className="border border-gray-200 rounded p-3 bg-white space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                  <input type="text" value={t.name || ''} onChange={(e) => updateTestimonial(idx, 'name', e.target.value)} placeholder="Student name" className="md:col-span-4 px-2 py-1 text-sm border border-gray-300 rounded" />
                  <input type="text" value={t.role || ''} onChange={(e) => updateTestimonial(idx, 'role', e.target.value)} placeholder="Role/Job title" className="md:col-span-4 px-2 py-1 text-sm border border-gray-300 rounded" />
                  <select value={t.rating || 5} onChange={(e) => updateTestimonial(idx, 'rating', parseInt(e.target.value))} className="md:col-span-3 px-2 py-1 text-sm border border-gray-300 rounded">
                    <option value={5}>★★★★★ 5 stars</option>
                    <option value={4}>★★★★ 4 stars</option>
                    <option value={3}>★★★ 3 stars</option>
                    <option value={2}>★★ 2 stars</option>
                    <option value={1}>★ 1 star</option>
                  </select>
                  <button type="button" onClick={() => removeTestimonial(idx)} className="md:col-span-1 text-red-600 hover:bg-red-50 rounded text-xs">×</button>
                </div>
                <textarea value={t.text || ''} onChange={(e) => updateTestimonial(idx, 'text', e.target.value)} placeholder="Review text..." rows={2} className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* ─── OUTCOMES (structured UI) ─── */}
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-gray-800">📊 Course Outcomes <span className="text-xs text-gray-500 font-normal">e.g., 85% career advancement</span></label>
            <button type="button" onClick={addOutcome} className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1">
              <Icon name="add" size={14} /> Add Outcome
            </button>
          </div>
          <div className="space-y-2">
            {(editForm.outcomes || []).map((o, idx) => (
              <div key={idx} className="border border-gray-200 rounded p-2 bg-white grid grid-cols-1 md:grid-cols-12 gap-2">
                <input type="text" value={o.metric || ''} onChange={(e) => updateOutcome(idx, 'metric', e.target.value)} placeholder="Metric (e.g., 85%)" className="md:col-span-3 px-2 py-1 text-sm border border-gray-300 rounded" />
                <input type="text" value={o.label || ''} onChange={(e) => updateOutcome(idx, 'label', e.target.value)} placeholder="Label (e.g., Career advancement)" className="md:col-span-8 px-2 py-1 text-sm border border-gray-300 rounded" />
                <button type="button" onClick={() => removeOutcome(idx)} className="md:col-span-1 text-red-600 hover:bg-red-50 rounded text-xs">×</button>
              </div>
            ))}
          </div>
        </div>
        
        <div className="md:col-span-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={editForm.certificate}
              onChange={(e) => setEditForm({...editForm, certificate: e.target.checked})}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">Provides Certificate</span>
          </label>
        </div>
      </div>
      
      <div className="flex gap-2">
        <button
          onClick={isCreate ? handleCreateCourse : () => handleUpdateCourse(editingCourse)}
          className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-lg shadow hover:from-green-700 hover:to-emerald-700 transition-all"
        >
          {isCreate ? 'Create Course' : 'Save Changes'}
        </button>
        <button
          onClick={handleCancelEdit}
          className="px-6 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-all"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {managingSlides ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <button
              onClick={() => setManagingSlides(null)}
              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
            >
              <Icon name="arrow_back" className="mr-2" />
              Back to Dashboard
            </button>
          </div>

          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Manage Slides: {managingSlides.topicTitle}</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setSlideView('builder')}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    slideView === 'builder'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Create Slides
                </button>
                <button
                  onClick={() => setSlideView('preview')}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    slideView === 'preview'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Preview
                </button>
              </div>
            </div>

            {slideView === 'builder' ? (
              <SlideBuilder
                courseId={managingSlides.courseId}
                moduleId={managingSlides.moduleId}
                topicId={managingSlides.topicId}
                topicTitle={managingSlides.topicTitle}
                onSave={() => setSlideView('preview')}
              />
            ) : (
              <SlidePreview
                courseId={managingSlides.courseId}
                moduleId={managingSlides.moduleId}
                topicId={managingSlides.topicId}
                onEdit={() => setSlideView('builder')}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Course Provider Dashboard</h1>
          <p className="mt-2 text-gray-600">Welcome back, {providerName}!</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Courses</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_courses}</p>
              </div>
              <Icon name="school" className="text-blue-500 text-3xl" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Purchases</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_purchases}</p>
              </div>
              <Icon name="shopping_cart" className="text-green-500 text-3xl" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">₹{stats.total_revenue.toLocaleString('en-IN')}</p>
              </div>
              <Icon name="payments" className="text-purple-500 text-3xl" />
            </div>
          </div>
        </div>

        {/* API Key Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-6 mb-8 border border-blue-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">API Integration</h3>
              <p className="text-sm text-gray-600 mb-3">
                Use this API key to post courses programmatically via our REST API
              </p>
              {showApiKey ? (
                <div className="bg-white p-3 rounded border border-gray-300 font-mono text-sm break-all">
                  {apiKey || 'Loading...'}
                </div>
              ) : (
                <button
                  onClick={() => setShowApiKey(true)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Show API Key
                </button>
              )}
            </div>
            <Icon name="key" className="text-blue-500 text-3xl ml-4" />
          </div>
        </div>

        {/* Create Course Button */}
        {!showCreateForm && !editingCourse && (
          <div className="mb-6">
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg shadow-lg hover:from-blue-700 hover:to-indigo-700 transition-all"
            >
              <Icon name="add" className="mr-2" />
              Create New Course
            </button>
          </div>
        )}

        {/* Create Course Form */}
        {showCreateForm && renderCourseForm(true)}

        {/* Courses List */}
        <div className="bg-white rounded-lg shadow mt-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">My Courses</h2>
              <div className="text-sm text-gray-600">
                Total: {courses.length} courses
              </div>
            </div>
          </div>

          <div className="p-6">
            {courses.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="school" className="text-gray-400 text-6xl mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Courses Yet</h3>
                <p className="text-gray-600 mb-6">Create your first course using the button above or via API</p>
              </div>
            ) : (
              <div className="space-y-4">
                {courses.map((course) => (
                  <div key={course.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                    {editingCourse === course.id ? (
                      renderCourseForm(false)
                    ) : (
                      <>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">{course.title}</h3>
                          {course.subtitle && (
                            <p className="text-sm text-gray-500 italic">{course.subtitle}</p>
                          )}
                          <p className="text-sm text-gray-600 mt-1">{course.description}</p>
                          <div className="flex items-center gap-4 mt-3 flex-wrap">
                            <span className="text-sm text-gray-500">
                              <Icon name="signal_cellular_alt" className="inline mr-1" />
                              {course.level}
                            </span>
                            {course.modules?.length > 0 && (
                              <span className="text-sm text-gray-500">
                                <Icon name="menu_book" className="inline mr-1" />
                                {course.modules.length} modules
                              </span>
                            )}
                            <span className="text-sm font-semibold text-primary">
                              ₹{course.price.toLocaleString('en-IN')}
                            </span>
                            <span className="text-sm text-gray-500">
                              <Icon name="category" className="inline mr-1" />
                              {course.category}
                            </span>
                            {course.certificate && (
                              <span className="text-sm text-green-600">
                                <Icon name="verified" className="inline mr-1" />
                                Certificate
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button 
                            onClick={() => {
                              // Extract first module and topic for slide management
                              const modules = course.modules || [];
                              if (modules.length > 0 && modules[0].topics) {
                                setManagingSlides({
                                  courseId: course.id,
                                  moduleId: `module_${modules[0].week || 1}`,
                                  topicId: `topic-1`,
                                  topicTitle: modules[0].topics[0] || modules[0].title
                                });
                              } else {
                                alert('Please add modules and topics to this course first');
                              }
                            }}
                            className="px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-all"
                          >
                            Manage Slides
                          </button>
                          <button 
                            onClick={() => handleEditCourse(course)}
                            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteCourse(course.id, course.title)}
                            className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Module-wise PPT Upload */}
                      {course.modules && course.modules.length > 0 && (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                            <Icon name="upload" className="text-purple-600" />
                            Upload PPT per Module
                            <span className="text-xs text-gray-400 font-normal">(AI will teach from these slides)</span>
                          </h4>
                          {pptUploadStatus && uploadingPPT?.courseId === course.id && (
                            <div className="mb-2 text-xs px-3 py-1.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                              {pptUploadStatus}
                            </div>
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {course.modules.map((module, modIdx) => {
                              const moduleId = `module_${module.week || modIdx + 1}`;
                              const isUploading = uploadingPPT?.courseId === course.id && uploadingPPT?.moduleId === moduleId;
                              return (
                                <div key={modIdx} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                                  <div className="flex-1 min-w-0 mr-2">
                                    <div className="text-xs font-medium text-gray-700 truncate">
                                      Module {module.week || modIdx + 1}: {module.title || 'Untitled'}
                                    </div>
                                    <div className="text-[10px] text-gray-400 truncate">
                                      {module.topics?.length || 0} topics
                                    </div>
                                  </div>
                                  <label className={`cursor-pointer inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded transition-colors shrink-0 ${isUploading ? 'bg-gray-300 text-gray-500 cursor-wait' : 'bg-purple-500 text-white hover:bg-purple-600'}`}>
                                    <Icon name="upload" />
                                    {isUploading ? 'Uploading...' : 'Upload PPT'}
                                    <input
                                      type="file"
                                      accept=".pptx"
                                      className="hidden"
                                      disabled={isUploading}
                                      onChange={(e) => {
                                        if (e.target.files?.[0]) {
                                          handlePPTUpload(course.id, moduleId, module.title || `Module ${modIdx + 1}`, e.target.files[0]);
                                          e.target.value = '';
                                        }
                                      }}
                                    />
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CourseProviderDashboard;
